/**
 * [2026-10-05] IPC 顶层参数键的**拼写双写**（snake_case ⇄ camelCase）。
 *
 * ## 为什么需要它
 *
 * 同一条命令的两种传输，对**顶层参数键的拼写要求相反**：
 *
 * - 桌面（Tauri IPC）：`#[tauri::command]` 宏把每个 Rust 形参名归一成 camelCase
 *   再用这个键去 JSON 体里取（默认 `ArgumentCase::Camel`，见
 *   `vendor/tauri-macros/src/command/wrapper.rs`；取键是
 *   `vendor/tauri/src/ipc/command.rs` 的 `v.get(self.key)`，**精确匹配、不做大小写归一**）。
 *   ⇒ 后端形参 `max_entries` 的键是 `maxEntries`，前端发 `max_entries` **取不到**。
 * - Web（内核 `/api/v1/system/invoke`）：`backend/src/handlers/kernel_bridge/mod.rs`
 *   里是**手写的** `read_arg("max_entries")` 逐个取键。
 *   ⇒ 前端发 `maxEntries` 同样**取不到**。
 *
 * 取不到键的后果不对称，两种都很糟：
 *
 * - `Option<T>` 形参 ⇒ 缺键被当成 `None`（`deserialize_option` 的 `None => visit_none()`）
 *   ⇒ **静默落默认值**。实测 `memory_report_prune(dry_run=true)` 因为发的是
 *   `dry_run` 而键要 `dryRun`，服务端 `dry_run.unwrap_or(false)` ⇒
 *   "演练/预览"真的执行了删除。界面点的是 dry-run，落盘是真删。
 * - 必填形参 ⇒ `command X missing required key Y` ⇒ 整个调用报错
 *   （`omniInvoke` 再降级成 null）。实测 `aether_unlock_management` 的
 *   `authorization_phrase` ⇒ 解锁按钮**永远失败**。
 *
 * ## 做法
 *
 * 在**唯一的桥接出口**给顶层键补齐另一种拼写（值相同）。两种传输都能取到，
 * 前端写哪种拼写都对；将来新增调用点也不必先查后端形参名。
 *
 * ## 边界（刻意的）
 *
 * - **只动顶层**。嵌套结构体的字段走 serde 原名，不属于 Tauri 的参数归一范围；
 *   往里塞别名会让 `deny_unknown_fields` 之类直接报错。
 * - 不改原对象（返回浅拷贝），调用方复用同一 args 对象不受影响。
 * - 幂等：已经两种拼写都在时不再补。
 * - 键不像标识符（带 `-`、`@` 等）时不猜；同一基准的两种拼写**值不一致**时不猜
 *   （两个都留着，交给后端各取各的）。
 */

const IDENT_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/** snake_case → camelCase。`max_entries` → `maxEntries`。 */
export function toCamelKey(key: string): string {
    return key.replace(/_+([a-zA-Z0-9])/g, (_, c: string) => c.toUpperCase());
}

/**
 * camelCase / PascalCase → snake_case。`maxEntries` → `max_entries`。
 *
 * 两段替换缺一不可：
 * - `([a-z0-9])([A-Z])` 处理 `nodeId` → `node_id`；
 * - `([A-Z]+)([A-Z][a-z])` 处理连续大写 `HTTPServer` → `http_server`。
 * 单个全大写词（`URL`）两段都不命中 ⇒ `url`，而不是 `u_r_l`。
 */
export function toSnakeKey(key: string): string {
    return key
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
        .toLowerCase();
}

/** 该键的另一种拼写；无需补时返回 `null`。 */
export function alternateArgKey(key: string): string | null {
    if (!IDENT_RE.test(key)) return null;
    const alt = key.includes('_') ? toCamelKey(key) : toSnakeKey(key);
    return alt === key ? null : alt;
}

/**
 * 返回一份给顶层键补齐另一种拼写的浅拷贝。
 * 非纯对象（`undefined` / 数组 / 原始值）原样返回，保持既有调用形态。
 */
export function withArgKeyAliases<T>(args: T): T {
    if (!args || typeof args !== 'object' || Array.isArray(args)) return args;
    const src = args as Record<string, unknown>;
    const out: Record<string, unknown> = { ...src };
    for (const key of Object.keys(src)) {
        const alt = alternateArgKey(key);
        if (alt === null) continue;
        if (alt in out) continue; // 两种拼写都已存在：不动，避免覆盖调用方显式给的值
        out[alt] = src[key];
    }
    return out as T;
}
